class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.layoutViewportHeight = window.innerHeight;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;
    this.updateSearchPosition = this.updateSearchPosition.bind(this);
    window.addEventListener('resize', this.updateSearchPosition);
    window.visualViewport?.addEventListener('resize', this.updateSearchPosition);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.toggleSearchState(false);
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.classList.contains('predictive-search-open')) return;
      const links = [...this.querySelectorAll('.search__results .product-card__title a, .search__box-others a, .search__results-all button')];
      if (!links.length) return;
      event.preventDefault();
      let index = links.indexOf(document.activeElement);
      index = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
      links[index].focus();
      links[index].scrollIntoView({block: 'nearest'});
    });

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  loadBestSellers() {
    if (this.bestSellersLoading) return;
    const target = this.querySelector('[data-discovery-products]');
    if (!target) return;
    this.bestSellersLoading = true;
    target.setAttribute('aria-busy', 'true');
    const url = new URL(target.dataset.collectionUrl, window.location.origin);
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    fetch(url).then(response => {
      if (!response.ok) throw new Error('Best sellers unavailable');
      return response.text();
    }).then(html => {
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-discovery-products]');
      if (!content || content.querySelector('[data-discovery-loading]')) throw new Error('Best sellers unavailable');
      target.innerHTML = content.innerHTML;
      target.setAttribute('aria-busy', 'false');
    }).catch(() => {
      this.bestSellersLoading = false;
      target.setAttribute('aria-busy', 'false');
      const status = target.querySelector('[data-discovery-loading]');
      if (status) {
        status.textContent = 'Unable to load best sellers. ';
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.textContent = 'Try again';
        retry.addEventListener('click', () => {
          status.textContent = 'Loading best sellers…';
          this.loadBestSellers();
        });
        status.append(retry);
      }
    });
  }

  onFocus(event) {
    this.loadBestSellers();
    this.updateSearchPosition();
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  updateSearchPosition() {
    if (!this.isHeaderSearch) return;
    // Keep the layout viewport stable while the software keyboard resizes the visual viewport.
    if (document.activeElement !== this.input) this.layoutViewportHeight = window.innerHeight;
    this.layoutViewportHeight = Math.max(this.layoutViewportHeight, window.innerHeight);
    const bottom = Math.ceil(this.input.getBoundingClientRect().bottom);
    const headerBottom = Math.ceil(this.closest('.header__top')?.getBoundingClientRect().bottom || bottom);
    document.documentElement.style.setProperty('--search-overlay-top', `${headerBottom}px`);
    document.documentElement.style.setProperty('--search-panel-top', `${headerBottom}px`);
    document.documentElement.style.setProperty('--viewport-height', `${this.layoutViewportHeight}px`);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.searchContent?.classList.remove('hidden');
    this.toggleSearchState(true);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('resources[type]', 'product,query,collection'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        if (!this.getQuery() || !this.classList.contains(this.states.OPEN)) return;
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.updateSearchPosition();
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(false);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
